import { useEffect, useState } from 'react';
import { api, lessons as Api, type Course, type Lesson, type LessonAccess, type LessonInput, type PubState } from '@tvx/api-client';
import { Modal } from '../components/Modal';
import { ImagePick } from '../components/ImagePick';

/* Nothing is sold by tier any more: a lesson either sells the course as a
   preview, or it is part of what the buyer paid for. */
const ACCESS_LABEL: Record<string, string> = {
  Free: 'Free preview — anyone',
  Paid: 'Buyers of this course',
};

/** Minutes are what a person types; seconds are what the API stores. */
const toMinutes = (sec: number) => (sec ? String(Math.round(sec / 60)) : '');

export function LessonForm({ lesson, courseId, courses, nextSort, onCancel, onSaved }: {
  lesson: Lesson | null;
  courseId: number;
  courses: Course[];
  nextSort: number;
  onCancel: () => void;
  onSaved: () => Promise<void>;
}) {
  /* `lesson` carries the joined thumb_path/course_title the API sends back;
     only the fields the API takes are kept, so a save cannot post them back. */
  const [f, setF] = useState<LessonInput>(() => lesson ? {
    course_id: lesson.course_id, slug: lesson.slug, title: lesson.title, summary: lesson.summary,
    duration_sec: lesson.duration_sec, access: lesson.access, video_url: lesson.video_url,
    thumb_media_id: lesson.thumb_media_id, sort: lesson.sort, state: lesson.state,
  } : {
    course_id: courseId, slug: '', title: '', summary: '', duration_sec: 0,
    access: 'Paid', video_url: '', thumb_media_id: null, sort: nextSort, state: 'draft',
  });
  const [minutes, setMinutes] = useState(() => toMinutes(lesson?.duration_sec ?? 0));
  const [thumb, setThumb] = useState<string | null>(lesson?.thumb_path ?? null);
  const [accessLevels, setAccessLevels] = useState<LessonAccess[]>(['Free', 'Paid']);
  const [status, setStatus] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    void api.get<{ lessonAccess: LessonAccess[] }>('/meta')
      .then((m) => m.lessonAccess && setAccessLevels(m.lessonAccess)).catch(() => {});
  }, []);

  const set = <K extends keyof LessonInput>(k: K, v: LessonInput[K]) => setF((p) => ({ ...p, [k]: v }));

  async function submit(state: PubState) {
    if (!f.title.trim()) { setError('A lesson title is required.'); return; }
    setError('');
    setStatus(state === 'published' ? 'Publishing…' : 'Saving…');
    try {
      const body = { ...f, state, duration_sec: Math.round((Number(minutes) || 0) * 60) };
      if (lesson) await Api.update(lesson.id, body);
      else await Api.create(body);
      await onSaved();
    } catch (e) { setStatus(''); setError((e as Error).message); }
  }

  async function remove() {
    if (!lesson) return;
    if (!confirm(`Delete “${lesson.title}”?`)) return;
    try { await Api.remove(lesson.id); await onSaved(); }
    catch (e) { setError((e as Error).message); }
  }

  return (
    <Modal
      title={lesson ? `Edit lesson · ${lesson.title}` : 'New lesson'}
      onClose={onCancel}
      footer={
        <>
          <span style={{ fontSize: 12, color: 'var(--tx3)' }}>{status}</span>
          {lesson ? (
            <button className="b bo" type="button" style={{ marginRight: 'auto' }} onClick={() => void remove()}>Delete</button>
          ) : null}
          <span className="mdl-spacer" />
          <button className="b bo" type="button" onClick={onCancel}>Cancel</button>
          <button className="b bo" type="button" onClick={() => void submit('draft')}>Save draft</button>
          <button className="b bg" type="button" onClick={() => void submit('published')}>
            {lesson?.state === 'published' ? 'Save' : 'Publish'}
          </button>
        </>
      }
    >
      {error ? <div className="err">{error}</div> : null}

      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 14 }}>
        <div className="fld"><label className="lbl">Title</label>
          <input className="inp" value={f.title} autoFocus onChange={(e) => set('title', e.target.value)} /></div>
        <div className="fld"><label className="lbl">Module</label>
          <select className="inp" value={f.course_id} onChange={(e) => set('course_id', Number(e.target.value))}>
            {courses.map((c) => <option key={c.id} value={c.id}>{c.title}</option>)}
          </select></div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 14 }}>
        <div className="fld"><label className="lbl">Length (minutes)</label>
          <input className="inp" type="number" min={0} value={minutes} onChange={(e) => setMinutes(e.target.value)} /></div>
        <div className="fld"><label className="lbl">Who can watch</label>
          <select className="inp" value={f.access} onChange={(e) => set('access', e.target.value as LessonAccess)}>
            {accessLevels.map((a) => <option key={a} value={a}>{ACCESS_LABEL[a] ?? a}</option>)}
          </select>
          <p className="hint">{f.access === 'Free'
            ? 'Anyone can watch it, and it is listed in the sitemap for search.'
            : 'Only people who bought this course — or a Learning pass.'}</p>
        </div>
        <div className="fld"><label className="lbl">Order</label>
          <input className="inp" type="number" value={f.sort} onChange={(e) => set('sort', Number(e.target.value))} /></div>
      </div>

      <div className="fld"><label className="lbl">Video URL</label>
        <input className="inp" value={f.video_url} placeholder="r2:courses/forex-basic/01.mp4  or  stream:…  or  https://…"
          onChange={(e) => set('video_url', e.target.value)} />
        <p className="hint">The file's path in R2 (r2:…), a Stream id (stream:…) or a full https:// link. A lesson with no video still lists, and says it is not ready.</p>
      </div>

      <ImagePick label="Lesson thumbnail" path={thumb} alt={f.title}
        hint="The still shown before the video plays, and beside the lesson in the course outline. Shown whole, so nothing is cropped."
        onPick={(id, p) => { set('thumb_media_id', id); setThumb(p); }} />

      <div className="fld"><label className="lbl">Lesson description (shown in the course outline and on the lesson page)</label>
        <textarea className="inp ta" rows={3} value={f.summary} onChange={(e) => set('summary', e.target.value)} /></div>
    </Modal>
  );
}
